import type { Casings, Tubings } from "gql/api";

export type CasingAtDepth = Pick<
  Casings,
  | "interior_diameter_inches"
  | "outer_diameter_inches"
  | "drift_inches"
  | "coupling_outer_diameter_inches"
  | "pound_per_foot"
>;

export type TubingAtDepth = Pick<
  Tubings,
  | "interior_diameter_inches"
  | "outer_diameter_inches"
  | "drift_inches"
  | "coupling_outer_diameter_inches"
  | "pound_per_foot"
>;

type DepthSpan<T> = {
  startDepthFt: number;
  endDepthFt: number;
  item: T;
};

function findInSpans<T>(spans: DepthSpan<T>[], measuredDepthFt: number) {
  if (!spans.length) return null;
  const match = spans.find(
    span =>
      measuredDepthFt >= span.startDepthFt && measuredDepthFt <= span.endDepthFt
  );
  if (match) return match.item;
  // Past the deepest span - the bottom-most string still applies.
  return measuredDepthFt > spans[spans.length - 1].endDepthFt
    ? spans[spans.length - 1].item
    : spans[0].item;
}

/**
 * Which casing is in play at a measured depth. Same top/bottom + length rule the
 * cross section charts use, lifted out so the bending stress calcs can resolve
 * standoff (`omega = casingID / 2 - equipmentOuterRadius`, col Q) without pulling
 * in the whole cross section charting composable.
 *
 * The workbook holds a single casing ID constant in `D2`. We resolve the casing
 * by depth instead, because our data model has separate top and bottom sections.
 */
export function getCasingAtMD<T extends CasingAtDepth>(
  measuredDepthFt: number,
  wellProperties: {
    r_top_casing?: T | null;
    top_casing_length_feet?: number | null;
    r_bottom_casing?: T | null;
    bottom_casing_length_feet?: number | null;
  } | null,
  totalCasingLengthFt?: number
): T | null {
  if (!wellProperties) return null;

  const topCasing = wellProperties.r_top_casing;
  const bottomCasing = wellProperties.r_bottom_casing;
  const topLengthFt = wellProperties.top_casing_length_feet ?? 0;
  const bottomLengthFt = wellProperties.bottom_casing_length_feet ?? 0;

  const spans: DepthSpan<T>[] = [];
  if (topCasing && topLengthFt > 0) {
    spans.push({ startDepthFt: 0, endDepthFt: topLengthFt, item: topCasing });
  }
  if (bottomCasing) {
    spans.push({
      startDepthFt: topLengthFt,
      endDepthFt:
        bottomLengthFt > 0
          ? topLengthFt + bottomLengthFt
          : totalCasingLengthFt ?? Number.MAX_SAFE_INTEGER,
      item: bottomCasing
    });
  }
  // Single-casing wells only populate the top slot.
  if (!spans.length && topCasing) {
    spans.push({
      startDepthFt: 0,
      endDepthFt: totalCasingLengthFt ?? Number.MAX_SAFE_INTEGER,
      item: topCasing
    });
  }

  return findInSpans(spans, measuredDepthFt);
}

/**
 * Which tubing is in play at a measured depth. Drives the string weight per foot
 * and the tubing joint length used by cols G/H/J.
 */
export function getTubingAtMD<T extends TubingAtDepth>(
  measuredDepthFt: number,
  wellProperties: {
    r_top_tubing?: T | null;
    top_tubing_length_feet?: number | null;
    r_bottom_tubing?: T | null;
    bottom_tubing_length_feet?: number | null;
  } | null,
  totalTubingLengthFt?: number
): T | null {
  if (!wellProperties) return null;

  const topTubing = wellProperties.r_top_tubing;
  const bottomTubing = wellProperties.r_bottom_tubing;
  const topLengthFt = wellProperties.top_tubing_length_feet ?? 0;
  const bottomLengthFt = wellProperties.bottom_tubing_length_feet ?? 0;

  const spans: DepthSpan<T>[] = [];
  if (topTubing && topLengthFt > 0) {
    spans.push({ startDepthFt: 0, endDepthFt: topLengthFt, item: topTubing });
  }
  if (bottomTubing) {
    spans.push({
      startDepthFt: topLengthFt,
      endDepthFt:
        bottomLengthFt > 0
          ? topLengthFt + bottomLengthFt
          : totalTubingLengthFt ?? Number.MAX_SAFE_INTEGER,
      item: bottomTubing
    });
  }
  if (!spans.length && topTubing) {
    spans.push({
      startDepthFt: 0,
      endDepthFt: totalTubingLengthFt ?? Number.MAX_SAFE_INTEGER,
      item: topTubing
    });
  }

  return findInSpans(spans, measuredDepthFt);
}
